import { alpha, Box, Divider, Typography, useTheme } from "@mui/material";
import MomentSvg from "../../../svg/MomentSvg";
import { ViewBlockHeader } from "./ViewBlockHeader";
import BlockItem from "./BlockItem";
import DataTags from "./DataTags";
import useNodeContext from "../../../hooks/useNodeContext";
import { useNodeInfoContext } from "../../../context/NodeInfoContext";
import { isDefined } from "@convin/utils/helper/common.helper";
import { MomentBlockFormType } from "../../../../types/bert";
import EmptyState from "@convin/components/custom_components/reuseable/EmptyState";
import EmptyDataSvg from "@convin/components/svg/EmptyDataSvg";
import { momentLocationOptions } from "../../../constants";
import AttachedLmsDocuments from "./attached-lms-docs/AttachedLmsDocuments";

const Moment = () => {
    const theme = useTheme();
    const { nodeDataToUpdate } = useNodeContext();
    const { nodeType } = useNodeInfoContext();

    const momentToUpdate = isDefined(nodeDataToUpdate)
        ? (nodeDataToUpdate as MomentBlockFormType<string[]>)
        : null;

    const moment = isDefined(momentToUpdate)
        ? momentToUpdate
        : ({
              name: "",
              location: "anywhere",
              speaker: "anyone",
              window: 1,
              phrase: [],
              prompt: null,
              is_prompt: nodeType === "condition_gpt" ? true : false,
              lms_reference_doc_ids: [],
              lms_reference_category_ids: [],
              id: null,
              start_time: null,
              end_time: null,
              lms_expression: "",
          } satisfies MomentBlockFormType<string[]>);

    if (!moment) {
        return (
            <EmptyState Component={<EmptyDataSvg />} text="No Moment Found!" />
        );
    }
    return (
        <div>
            <ViewBlockHeader icon={<MomentSvg />} title="Moment" />
            <Box
                className="flex flex-col "
                sx={{
                    p: 2,
                    gap: 1.5,
                }}
            >
                <BlockItem title="Moment Name" value={moment?.name} />
                <Divider />
                {moment?.lms_expression && (
                    <>
                        <BlockItem
                            title="LMS Expression"
                            value={moment?.lms_expression}
                        />
                        <Divider />
                    </>
                )}
                {moment.is_prompt ? (
                    <>
                        <BlockItem title="Prompt" value={moment?.prompt} />
                        <Divider />
                        <AttachedLmsDocuments
                            files={moment?.lms_reference_doc_ids ?? []}
                            folders={moment?.lms_reference_category_ids ?? []}
                        />
                    </>
                ) : (
                    <>
                        <BlockItem
                            title="Phrases"
                            Component={
                                <DataTags
                                    drawerTitle="Added Phrases"
                                    items={(moment?.phrase || []) as string[]}
                                />
                            }
                            value="Phrases"
                        />
                        <Divider />
                        <div className="flex justify-between">
                            <BlockItem
                                title="Spoken by"
                                value={moment?.speaker}
                            />
                            <BlockItem title="Window" value={moment?.window} />
                        </div>
                        <Divider />
                        {moment?.location === "anywhere" ? (
                            <BlockItem title="Location" value="Anywhere" />
                        ) : (
                            <div className="flex flex-col gap-1">
                                <BlockItem
                                    title="Location"
                                    value={moment?.location}
                                />
                                <Box
                                    sx={{
                                        backgroundColor: alpha(
                                            theme.palette.textColors[999],
                                            0.1
                                        ),
                                        p: 1,
                                        gap: 0.5,
                                        fontWeight: 600,
                                        display: "flex",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant="textXs"
                                        color={"textColors.666"}
                                        fontWeight={"inherit"}
                                    >
                                        Detect this moment
                                    </Typography>
                                    <Typography
                                        variant="textXs"
                                        color={"primary.main"}
                                        fontWeight={"inherit"}
                                    >
                                        {moment?.seconds} secs from{" "}
                                        {
                                            momentLocationOptions.find(
                                                (e) =>
                                                    e.id ===
                                                    moment?.from +
                                                        "." +
                                                        moment?.operation
                                            )?.label
                                        }
                                    </Typography>
                                </Box>
                            </div>
                        )}
                    </>
                )}
            </Box>
        </div>
    );
};

export default Moment;
